<template>
  <div v-if="showSavedScheduledRetries">
    <div class="card">
      <div class="card-body">
        <div class="card-title">{{ trans('dashboard.integrations.flows.admin.scheduled_retries.title') }} ( {{ totalScheduledRetries }} )</div>
        <table class="table w-full mt-6" aria-label="List of planned entities to retry">
          <thead>
          <tr>
            <th scope="col" class="w-1/6" v-if="userCanDoActions">
              <select class="form-control form-select form-control-sm" name="actions" @change="executeAction"
                      v-model="actionToPerform"
                      :disabled="scheduledRetriesToPerformAction.length <= 0">
                <option :value=null selected disabled>{{ trans('dashboard.integrations.flows.select_an_action') }}</option>
                <option value="delete">{{ trans('common.delete') }}</option>
                <option v-if="hasSelectedAll" value="delete_all">{{ trans('dashboard.integrations.flows.admin.delete_all') }}</option>
              </select>
            </th>
            <th scope="col" class="w-1/3">
              <input
                :value="filters.retry_entity_id"
                class="form-input-bordered form-control form-control-sm"
                @change="(event) => onFilterChange({ retry_entity_id: event.target.value })"
              />
            </th>
            <th scope="col" class="w-1/6"></th>
            <th scope="col" class="w-1/6"></th>
            <th scope="col" class="w-1/6" v-if="userCanDoActions"></th>
          </tr>

          <tr>
            <th scope="col" class="text-center" v-if="userCanDoActions">
              <input type="checkbox" :checked="hasSelectedAll"
                     @change="toggleSelectAll" :disabled="!this.scheduledRetries.length"/>
            </th>
            <th scope="col" class="text-center">{{ flowName + ' ' + trans('common.id') }}</th>
            <th scope="col" class="text-left">{{ trans('dashboard.integrations.flows.admin.scheduled_retries.next_retry_at') }}</th>
            <th scope="col" class="text-center">{{ trans('dashboard.integrations.flows.admin.scheduled_retries.reason') }}</th>
            <th scope="col" class="text-center" v-if="userCanDoActions">{{ trans('common.action') }}</th>
          </tr>
          </thead>

          <tbody>
          <tr v-if="loadingScheduledRetries">
            <td colspan="8" v-if="userCanDoActions">
              <div class="text-center">
                <loader class="text-60"/>
              </div>
            </td>
          </tr>
          <tr v-for="scheduledRetry in scheduledRetries" v-else :key="scheduledRetry.id">
            <td v-if="userCanDoActions">
              <div class="text-center">
                <input type="checkbox" v-model="scheduledRetriesToPerformAction" :value="scheduledRetry.id"/>
              </div>
            </td>
            <td>
              <div class="text-center">
                <span class="whitespace-no-wrap">{{ scheduledRetry.entity_id }}</span>
              </div>
            </td>
            <td>
              <div class="text-center">
                <span class="whitespace-no-wrap">{{ scheduledRetry.scheduled_at }}</span>
              </div>
            </td>
            <td>
              <div class="text-center">
                <span class="whitespace-no-wrap">{{ scheduledRetry.reasonText }}</span>
              </div>
            </td>
            <td v-if="userCanDoActions">
              <div class="text-center">
                <form-button
                  button-class="btn btn-danger btn-sm"
                  role="button"
                  :value="trans('common.stop')"
                  :processing="loadingDelete"
                  @click="deleteSingleEntity(scheduledRetry.id)"/>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
        <pagination v-if="pagination" :pagination="pagination" @paginate="onFilterChange"/>
      </div>
    </div>
    <confirmation-modal
      :show-modal="confirmingAction"
      :header="trans('dashboard.integrations.flows.confirm_action')"
      :content="trans(`dashboard.integrations.flows.${actionToPerform}_items_confirmation_description`)"
      :confirm-button-text="confirmActionButton"
      :type="['delete', 'delete_all'].includes(actionToPerform) ? 'danger' : 'primary'"
      @close="handleCloseActionModal"
      @confirm="handleConfirmAction"
    />
  </div>
</template>
<script>
import FormButton from "@/components/Shared/Form/Button"
import Loader from "@/components/Shared/Loader"
import Pagination from '@/components/Shared/Pagination'
import DashboardLayout from "@/Layouts/DashboardLayout.vue";
import IntegrationFlowLayout from "@/Layouts/IntegrationFlowLayout.vue";
import {mapGetters} from "vuex";
import ConfirmationModal from "@/components/Dashboard/ConfirmationModal.vue";
import HoverIcon from "@/components/Shared/HoverIcon.vue";

export default {
  name: 'FlowScheduledRetry',

  layout: [DashboardLayout, IntegrationFlowLayout],

  components: {
    HoverIcon,
    ConfirmationModal,
    FormButton,
    Loader,
    Pagination
  },

  props: {
    user: Object,
    flowName: String,
    readOnly: Boolean,
  },

  data() {
    return {
      initialLoading: true,
      loadingScheduledRetries: false,
      loadingDelete: false,
      loadingRetry: false,
      totalScheduledRetries: '-',
      scheduledRetries: [],
      hasCachingFile: null,
      hasErrorAfterFetchingCachingFile: false,
      pagination: null,
      toBeDeleted: null,
      scheduledRetriesToPerformAction: [],
      actionToPerform: null,
      confirmingAction: false,
      hasSelectedAll: false,
      displayRetryFunction: true
    };
  },

  async beforeMount() {
    Promise
      .all([this.getScheduledRetries()])
      .then(() => this.initialLoading = false)
  },

  computed: {
    ...mapGetters('flowsDetails', ['userIntegrationFlow']),

    filters() {
      const {retry_page, retry_entity_id = null} = this.$route().params;
      return {
        retry_page: Number(retry_page || '1'),
        retry_entity_id: retry_entity_id,
      };
    },

    showSavedScheduledRetries() {
      return this.scheduledRetries.length > 0 || this.filters.retry_entity_id !== null || this.toBeDeleted !== null;
    },
    userCanDoActions() {
      return !this.readOnly;
    },

    confirmActionButton() {
      switch (this.actionToPerform) {
        case 'delete_all':
          return this.trans('dashboard.integrations.flows.admin.delete_all')
        default:
          return this.trans(`common.${this.actionToPerform}`)
      }
    },
  },

  watch: {
    filters() {
      this.getScheduledRetries();
    },

    scheduledRetriesToPerformAction() {
      this.hasSelectedAll = this.scheduledRetries.length > 0 && this.scheduledRetriesToPerformAction.length === this.scheduledRetries.length;
    },
  },

  methods: {
    async getScheduledRetries() {
      this.loadingScheduledRetries = true;

      return axios
        .get(route('api.integrations.flows.admin.entities.retries.index', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]), {
          params: {...this.filters, ...(this.additionalFilters || {})},
        })
        .then(({data}) => {
          const {data: scheduledRetries, ...rest} = data;
          this.scheduledRetries = scheduledRetries;
          this.pagination = rest;
          this.totalScheduledRetries = rest.total;
        })
        .finally(() => this.loadingScheduledRetries = false);
    },

    onFilterChange(data) {
      let page = data.page
      delete data['page'];
      data.retry_page = page

      const filters = {...this.filters, ...data};
      const queryParams = {};
      Object.keys(filters).forEach(filter => {
        if (filters[filter] !== null && filters[filter] !== '—') {
          queryParams[filter] = filters[filter];
        }
      });

      this.$inertia.visit(
        this.$route(this.$route().current(), this.$route().params),
        {data: queryParams}
      )
    },

    async deleteScheduledRetries(retryId) {
      this.toBeDeleted = retryId;
      this.loadingDelete = true;
      this.scheduledRetries = retryId !== 'ALL' ?
        _.filter(this.scheduledRetries, retry => !retryId.includes(retry.id))
        : [];

      await axios
        .delete(route('api.integrations.flows.admin.entities.retries.delete', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]), {data: {retryIds: retryId}})
        .then(() => this.loadingDelete = false);
    },

    executeAction(event) {
      this.actionToPerform = event.target.value;
      this.confirmingAction = true;
    },

    deleteSingleEntity(retryIds) {
      this.scheduledRetriesToPerformAction = [retryIds];
      this.actionToPerform = 'delete';
      this.confirmingAction = true;
    },

    handleConfirmAction() {
      if (['delete', 'delete_all'].includes(this.actionToPerform)) {
        this.deleteScheduledRetries(this.actionToPerform === 'delete_all' ? 'ALL' : this.scheduledRetriesToPerformAction);
      } else if (this.actionToPerform === 'retry') {
        this.triggerFlow(this.scheduledRetriesToPerformAction);
      }

      this.handleCloseActionModal();
    },

    handleCloseActionModal() {
      this.confirmingAction = false;
      this.actionToPerform = null;
      this.scheduledRetriesToPerformAction = [];
    },

    toggleSelectAll() {
      this.scheduledRetriesToPerformAction = this.hasSelectedAll ? [] : this.scheduledRetries.map((retry) => retry.id)
    },
  },
}
</script>
